import React, { useState, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

import CustomSelect from "src/app/components/CustomSelect/CustomSelect";
import { translateMessage } from "src/app/methods/translateMessage";
import AvatarPlaceholder from "src/images/avatar-purple.svg";
import { Img } from "src/app/components/Img/Img";
import { API_URLS } from "src/utils/API_URLS";
import { tableDataType } from "src/app/components/Table/Table";
import { wsAxiosGet } from "src/helpers/wsAxios";
import {
  SkeletonCircle,
  SkeletonText,
} from "src/app/components/Skeleton/Skeleton";

import "./MemberSelect.scss";

function resolveValueFromFilters(filters, colUuid, options) {
  return (
    filters[colUuid]?.value
      ?.map((v) => options.find((o) => o.value.id === v))
      .filter(Boolean) ?? []
  );
}

export function MemberSelect({ col, sendFilter, filters, dataType }) {
  const intl = useIntl();
  const activeMembersList = useSelector(
    (state) => state.projectReducer.activeMembersList,
  );
  const activeWorkspaceUuid = useSelector(
    (state) => state.mainReducer.activeWorkspaceUuid,
  );
  const wsTeamUuids = col?.data?.wsTeamUuids ?? [];
  const hasWsTeamUuids = wsTeamUuids.length > 0;
  const uuid = col?.uuid;

  const [teamMemberUuids, setTeamMemberUuids] = useState([]);
  const [isLoading, setIsLoading] = useState(hasWsTeamUuids);

  const [value, setValue] = useState([]);

  const options = useMemo(() => {
    const members = hasWsTeamUuids
      ? activeMembersList.filter(
          (member) =>
            !member?.hideOnMembersList && teamMemberUuids.includes(member.id),
        )
      : activeMembersList.filter(
          (member) =>
            !member?.hideOnMembersList && member?.status !== "removed",
        );

    return members.map((member) => ({
      value: { email: member.email, name: member.name, id: member.id },
      label: (
        <span className="activity-filters__option">
          <Img
            className="activity-filters__option-image"
            src={member?.avatarUrl}
            fallbackImageSrc={AvatarPlaceholder}
            alt="avatar"
          />
          <span className="activity-filters__option-text">{member.name}</span>
        </span>
      ),
    }));
  }, [activeMembersList, teamMemberUuids, hasWsTeamUuids]);

  const filterOption = (candidate, input) => {
    return (
      candidate.data.__isNew__ ||
      candidate.value.name.toUpperCase().includes(input.toUpperCase())
    );
  };

  useEffect(() => {
    if (!hasWsTeamUuids) return;

    const controller = new AbortController();

    const checkMemberFilterByTeam = () => {
      let url = null;

      switch (dataType) {
        case tableDataType.GlobalTask:
          url = API_URLS.globalTaskMetaFieldTeamMembers;
          break;
        case tableDataType.Project:
          // Campaigns table columns are global-project metafields.
          // Using project endpoint here causes 400 + error toast for member fields.
          url = API_URLS.globalProjectMetaFieldTeamMembers;
          break;

        default:
          console.error("Data type not supported");
          return;
      }

      url = url
        .replace(":wsMetaFieldUuid:", uuid)
        .replace(":wsWorkspaceUuid:", activeWorkspaceUuid);

      wsAxiosGet({
        url,
        config: { signal: controller.signal },
      })
        .then((response) => {
          const data = response?.content ?? [];
          setTeamMemberUuids(data);
        })
        .catch(() => {
          setTeamMemberUuids([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    checkMemberFilterByTeam();

    return () => controller.abort();
  }, [activeWorkspaceUuid, uuid, dataType, hasWsTeamUuids]);

  useEffect(() => {
    setValue(resolveValueFromFilters(filters, col.uuid, options));
  }, [filters, col.uuid, options]);

  const skeletonOptions = [0, 1, 2].map((i) => ({
    value: { id: `skeleton-${i}`, name: "", email: "" },
    label: (
      <span className="activity-filters__option">
        <SkeletonCircle size={24} marginRight={8} />
        <SkeletonText height={12} width={120} />
      </span>
    ),
    isDisabled: true,
  }));

  return (
    <CustomSelect
      className="projects-table-filters__member-select ws-react-select ws-react-select--with-tiles"
      value={value}
      onChange={(nv) => {
        setValue(nv);
        sendFilter(
          col.uuid,
          nv.map((v) => v.value.id),
        );
      }}
      placeholder={translateMessage({
        intl,
        id: "ws_all",
        defaultMessage: "All",
      })}
      options={isLoading ? skeletonOptions : options}
      isMulti
      withTiles
      openMenuOnFocus={false}
      isSearchable={!isLoading}
      filterOption={filterOption}
      data-qa={col.name}
    />
  );
}
